// Funzioni pure di calcolo Squad Score.
import type { SessionResponse, MatchPlayerStat, SquadScoreSetting } from "../types";
import { currentIntlLocale } from "./i18n";

export function isCurrentMonth(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

export function currentMonthLabel(): string {
  return new Date().toLocaleDateString(currentIntlLocale(), { month: "long" });
}

const MS_PER_DAY = 86_400_000;

function decayWeight(iso: string | null | undefined, halfLifeDays: number | null | undefined, nowMs: number): number {
  if (!halfLifeDays || halfLifeDays <= 0) return 1;
  if (!iso) return 1;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 1;
  const ageDays = Math.max(0, (nowMs - t) / MS_PER_DAY);
  return Math.pow(0.5, ageDays / halfLifeDays);
}

function matchOutcomePoints(stat: MatchPlayerStat, setting: SquadScoreSetting): number {
  const us = stat.match_our_score;
  const them = stat.match_opponent_score;
  if (us == null || them == null) return 0;
  if (us > them) return setting.win_bonus || 0;
  if (us < them) return setting.loss_penalty || 0;
  return setting.draw_points || 0;
}

function responsePoints(response: SessionResponse, setting: SquadScoreSetting): number {
  if (response.status === "present") {
    return response.session_has_matches ? setting.match_day_present_points : setting.training_present_points;
  }
  if (response.status === "late") return setting.late_penalty;
  if (response.status === "absent") return setting.absence_penalty;
  return setting.no_response_penalty;
}

function statPoints(stat: MatchPlayerStat, setting: SquadScoreSetting): number {
  const goals = (stat.goals || 0) * setting.goal_points;
  const assists = (stat.assists || 0) * setting.assist_points;
  const yellows = (stat.yellow_cards || 0) * (setting.yellow_card_penalty || 0);
  const reds = (stat.red_cards || 0) * (setting.red_card_penalty || 0);
  return goals + assists + yellows + reds + matchOutcomePoints(stat, setting);
}

export type SquadScoreBreakdown = {
  score: number;
  eventsCount: number;
  avgPerEvent: number;
  responseScore: number;
  statScore: number;
  minEventsPenalty: number;
};

export function calculateSquadScoreDetailed(
  responses: SessionResponse[],
  stats: MatchPlayerStat[],
  setting: SquadScoreSetting,
  options?: { minEventsOverride?: number; now?: number },
): SquadScoreBreakdown {
  const nowMs = options?.now ?? Date.now();
  const halfLife = setting.decay_half_life_days ?? null;

  const responseScore = responses.reduce((total, response) => {
    const w = decayWeight(response.session_starts_at, halfLife, nowMs);
    return total + responsePoints(response, setting) * w;
  }, 0);

  const statScore = stats.reduce((total, stat) => {
    const w = decayWeight(stat.match_starts_at, halfLife, nowMs);
    return total + statPoints(stat, setting) * w;
  }, 0);

  const minEvents = options?.minEventsOverride ?? setting.min_events;
  const minEventsPenalty =
    responses.length < minEvents
      ? (minEvents - responses.length) * setting.no_response_penalty
      : 0;

  const score = responseScore + statScore + minEventsPenalty;
  const eventsCount = responses.length;
  const avgPerEvent = eventsCount > 0 ? score / eventsCount : 0;

  return { score, eventsCount, avgPerEvent, responseScore, statScore, minEventsPenalty };
}

export function calculateSquadScore(
  responses: SessionResponse[],
  stats: MatchPlayerStat[],
  setting: SquadScoreSetting,
  now?: number,
) {
  return calculateSquadScoreDetailed(responses, stats, setting, { now }).score;
}

export function calculateMonthlySquadScore(
  responses: SessionResponse[],
  stats: MatchPlayerStat[],
  setting: SquadScoreSetting,
  totalSessionsInMonth?: number,
) {
  const monthResponses = responses.filter((r) => isCurrentMonth(r.session_starts_at));
  const monthStats = stats.filter((s) => isCurrentMonth(s.match_starts_at));
  const cappedMinEvents =
    totalSessionsInMonth != null ? Math.min(setting.min_events, totalSessionsInMonth) : setting.min_events;
  return calculateSquadScoreDetailed(monthResponses, monthStats, setting, {
    minEventsOverride: cappedMinEvents,
  }).score;
}

export function calculateMonthlySquadScoreDetailed(
  responses: SessionResponse[],
  stats: MatchPlayerStat[],
  setting: SquadScoreSetting,
  totalSessionsInMonth?: number,
): SquadScoreBreakdown {
  const monthResponses = responses.filter((r) => isCurrentMonth(r.session_starts_at));
  const monthStats = stats.filter((s) => isCurrentMonth(s.match_starts_at));
  const cappedMinEvents =
    totalSessionsInMonth != null ? Math.min(setting.min_events, totalSessionsInMonth) : setting.min_events;
  return calculateSquadScoreDetailed(monthResponses, monthStats, setting, {
    minEventsOverride: cappedMinEvents,
  });
}

export function calculatePeriodSquadScore(
  responses: SessionResponse[],
  stats: MatchPlayerStat[],
  setting: SquadScoreSetting,
  range: { fromMs: number | null; toMs: number | null },
  totalSessionsInPeriod?: number,
  now?: number,
) {
  const inRange = (iso?: string | null) => {
    if (!iso) return range.fromMs === null && range.toMs === null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return false;
    if (range.fromMs !== null && t < range.fromMs) return false;
    if (range.toMs !== null && t > range.toMs) return false;
    return true;
  };
  const filteredResponses = responses.filter((r) => inRange(r.session_starts_at));
  const filteredStats = stats.filter((s) => inRange(s.match_starts_at));
  const cappedMin =
    totalSessionsInPeriod != null ? Math.min(setting.min_events, totalSessionsInPeriod) : setting.min_events;
  const breakdown = calculateSquadScoreDetailed(filteredResponses, filteredStats, setting, {
    minEventsOverride: cappedMin,
    now,
  });
  return {
    score: breakdown.score,
    eventsCount: breakdown.eventsCount,
    avgPerEvent: breakdown.avgPerEvent,
  };
}
